import type { paths } from "@/api-schema";
import { alphartisApiAxios } from "@/config/axios";
import { useQuery } from "@tanstack/react-query";
import { handlePaginatedParameters } from "../_helpers/handle-parameters";
import { optionsWithDefaultsPaginated } from "../_helpers/options-with-defaults";
import { PaginatedOptions, UseQueryOptionsNoKeyAndFn } from "../_types";

export const COMPANY_SEARCH_RESOURCE = "company" as const;
export const COMPANY_SEARCH_OPERATION = "search" as const;
export const COMPANY_SEARCH_API_PATH = `/v1/${COMPANY_SEARCH_RESOURCE}/${COMPANY_SEARCH_OPERATION}` as const;

export type CompanySearchParameters = paths[typeof COMPANY_SEARCH_API_PATH]["post"]["parameters"]["query"];
export type CompanySearchBody =
  paths[typeof COMPANY_SEARCH_API_PATH]["post"]["requestBody"]["content"]["application/json"];
export type CompanySearchResponse =
  paths[typeof COMPANY_SEARCH_API_PATH]["post"]["responses"][200]["content"]["application/json"];

export function useCompanySearchQuery({
  options,
  enabled,
  queryOptions,
}: {
  options?: PaginatedOptions<CompanySearchParameters, CompanySearchBody>;
  enabled?: boolean;
  queryOptions?: UseQueryOptionsNoKeyAndFn<CompanySearchResponse, Error, CompanySearchResponse, readonly unknown[]>;
} = {}) {
  const optionsWithDefaults =
    optionsWithDefaultsPaginated<PaginatedOptions<CompanySearchParameters, CompanySearchBody>>(options);

  const requestParameters = {
    ...(optionsWithDefaults.parameters || {}),
    ...handlePaginatedParameters(optionsWithDefaults.parameters),
  };

  return useQuery({
    queryKey: [
      COMPANY_SEARCH_RESOURCE,
      COMPANY_SEARCH_OPERATION,
      { parameters: requestParameters, body: optionsWithDefaults.body },
    ],
    queryFn: async () => {
      const response = await alphartisApiAxios.post<CompanySearchResponse>(
        COMPANY_SEARCH_API_PATH,
        optionsWithDefaults.body,
        {
          params: requestParameters,
        },
      );

      return response.data;
    },
    ...queryOptions,
    enabled,
  });
}
